import type { BulkSpotRequest, SpotUsage, UsageStatus, UsageType } from '../../../api/my-parking-spots';
import { dateOnlyToUtcEnd, dateOnlyToUtcStart } from '../../../utils/date-utils';
import {
  formatRequesterLabel,
  getUsageStatusLabel,
  getUsageTypeLabel,
} from '../../my-parking-spots/utils/usage-display';

export const ALL_USAGE_HISTORY_STATUSES: UsageStatus[] = [
  'PENDING',
  'ACTIVE',
  'FINISHED',
  'CANCELLED',
  'REJECTED',
];

export const ALL_USAGE_HISTORY_TYPES: UsageType[] = ['BORROW', 'RENT'];

export interface UsageHistoryFilters {
  search: string;
  startDate: string;
  endDate: string;
  statuses: UsageStatus[];
  types: UsageType[];
}

export const defaultUsageHistoryFilters: UsageHistoryFilters = {
  search: '',
  startDate: '',
  endDate: '',
  statuses: [...ALL_USAGE_HISTORY_STATUSES],
  types: [...ALL_USAGE_HISTORY_TYPES],
};

export const USAGE_HISTORY_STATUS_OPTIONS = ALL_USAGE_HISTORY_STATUSES.map((status) => ({
  value: status,
  label: getUsageStatusLabel(status),
}));

export const USAGE_HISTORY_TYPE_OPTIONS = ALL_USAGE_HISTORY_TYPES.map((type) => ({
  value: type,
  label: getUsageTypeLabel(type),
}));

function arraysEqual<T extends string>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((item) => setB.has(item));
}

export function isOwnerUsage(
  usage: SpotUsage,
  userEmail?: string | null,
  userId?: string | null,
): boolean {
  if (userEmail && usage.userEmail) {
    return usage.userEmail.toLowerCase() !== userEmail.toLowerCase();
  }
  if (userId) {
    return usage.userId !== userId;
  }
  return false;
}

export function filterUsagesByCondominium(
  usages: SpotUsage[],
  spotIdsInCondominium: Set<string>,
): SpotUsage[] {
  if (spotIdsInCondominium.size === 0) return [];
  return usages.filter((usage) => spotIdsInCondominium.has(usage.spotId));
}

export function applyUsageHistoryFilters(
  usages: SpotUsage[],
  filters: UsageHistoryFilters,
): SpotUsage[] {
  const search = filters.search.trim().toLowerCase();
  const selectedStatuses = new Set(filters.statuses);
  const selectedTypes = new Set(filters.types);
  const rangeStartMs = filters.startDate ? new Date(dateOnlyToUtcStart(filters.startDate)).getTime() : null;
  const rangeEndMs = filters.endDate ? new Date(dateOnlyToUtcEnd(filters.endDate)).getTime() : null;

  return usages.filter((usage) => {
    if (selectedStatuses.size > 0 && !selectedStatuses.has(usage.status)) return false;
    if (selectedTypes.size > 0 && !selectedTypes.has(usage.type)) return false;
    if (
      search &&
      !usage.spotCode?.toLowerCase().includes(search) &&
      !formatRequesterLabel(usage).toLowerCase().includes(search)
    ) {
      return false;
    }
    if (rangeStartMs != null && new Date(usage.startAt).getTime() < rangeStartMs) return false;
    if (rangeEndMs != null && new Date(usage.endAt).getTime() > rangeEndMs) return false;
    return true;
  });
}

export function sortUsagesByStartDesc(usages: SpotUsage[]): SpotUsage[] {
  return [...usages].sort(
    (a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime(),
  );
}

export function splitUsagesByStatus(usages: SpotUsage[]) {
  const pending = usages.filter((u) => u.status === 'PENDING');
  const active = usages.filter((u) => u.status === 'ACTIVE');
  const past = usages.filter((u) => u.status !== 'PENDING' && u.status !== 'ACTIVE');
  return { pending, active, past };
}

export function splitBulkRequests(requests: BulkSpotRequest[]) {
  return {
    active: requests.filter((r) => !r.expired),
    expired: requests.filter((r) => r.expired),
  };
}

export function hasActiveUsageHistoryFilters(filters: UsageHistoryFilters): boolean {
  return countActiveUsageHistoryFilters(filters) > 0;
}

export function countActiveUsageHistoryFilters(filters: UsageHistoryFilters): number {
  let count = 0;
  if (filters.search.trim().length > 0) count += 1;
  if (filters.startDate || filters.endDate) count += 1;
  if (!arraysEqual(filters.statuses, ALL_USAGE_HISTORY_STATUSES)) count += 1;
  if (!arraysEqual(filters.types, ALL_USAGE_HISTORY_TYPES)) count += 1;
  return count;
}
